import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import { IUser } from '../../types';

const SERVER_URL = API_CONFIG.SERVER_URL;

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  token: string;
  user: IUser;
  workspace?: any; // You might want to create a proper workspace type
}

interface JWTPayload {
  exp: number;
  iat: number;
  sub: string;
  [key: string]: any;
}

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Utility functions
export const getToken = (): string | null =>
  localStorage.getItem(STORAGE_KEYS.TOKEN);

export const getUser = (): IUser | null => {
  const userData = localStorage.getItem(STORAGE_KEYS.USER);
  return userData ? JSON.parse(userData) : null;
};

export const isAuthenticated = (): boolean => {
  const token = getToken();
  if (!token) return false;

  try {
    const payload: JWTPayload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp > currentTime;
  } catch (error) {
    return false;
  }
};

export const logout = (): void => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem(STORAGE_KEYS.WORKSPACE);
  window.location.href = '/';
};

export const updateToken = (token: string): void => {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
};

// Login function
export const login = async (
  credentials: LoginCredentials
): Promise<LoginResponse> => {
  try {
    const response = await api.post<LoginResponse>('auth/', credentials);

    // Store token and user data in localStorage
    if (response.data.token) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
    }
    if (response.data.user) {
      localStorage.setItem(
        STORAGE_KEYS.USER,
        JSON.stringify(response.data.user)
      );
    }
    if (response.data.workspace) {
      localStorage.setItem(
        STORAGE_KEYS.WORKSPACE,
        JSON.stringify(response.data.workspace)
      );
    }

    return response.data;
  } catch (error) {
    console.error('Error during login:', error);
    throw error;
  }
};

// Export the configured axios instance
export default api;
